// MARK://dhk8/pos/features/training/TrainingDataProvider
// Wires the data sandbox to the training-mode flag.
//
// When training mode flips ON we re-clone the pristine seeds into every preview
// client's module root (so the trainee starts clean); when it flips OFF we do it
// again (so the trainee's edits — orders opened, tickets closed, tables moved —
// disappear and the demo is back to pristine). The clients themselves are
// untouched: they already keep their stub state in module-level `let`s seeded
// from `structuredClone(SEED_*)`, so swapping that root *is* the sandbox.
//
// Outside the preview build (`VITE_API_STUB` off) there are no stub roots and
// `resetTrainingData()` is a near no-op — the provider still mounts so the rest
// of the training UI (banner, badge) has a stable home, but it does nothing
// observable. (Real-backend training-tenant routing is a separate, future job.)
//
// Mounted in App.tsx INSIDE <TrainingModeProvider> (it reads the flag).

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react'

import { useTrainingMode } from '../../lib/training-mode.js'
import { resetTrainingData } from '../../lib/training-data.js'

interface TrainingDataContextValue {
  /** True while the data clients are operating on the sandboxed (cloned) copy. */
  sandboxed: boolean
}

const TrainingDataContext = createContext<TrainingDataContextValue>({ sandboxed: false })

export function TrainingDataProvider({ children }: { children: ReactNode }) {
  const training = useTrainingMode()
  // Skip the very first effect run when training is already false (nothing to do)
  // but DO run it when the app boots straight into training mode (?mode=training).
  const prev = useRef<boolean | null>(null)

  useEffect(() => {
    const was = prev.current
    prev.current = training
    // Reset on every transition into or out of training mode, and on the initial
    // mount if we boot into training mode. Resetting on exit is what makes the
    // trainee's changes "no se guardan".
    if (was === training) {
      if (was === null && training) resetTrainingData()
      return
    }
    resetTrainingData()
  }, [training])

  return (
    <TrainingDataContext.Provider value={{ sandboxed: training }}>
      {children}
    </TrainingDataContext.Provider>
  )
}

/** Whether the data clients are currently sandboxed (training mode on). */
export function useTrainingSandbox(): boolean {
  return useContext(TrainingDataContext).sandboxed
}
